import React, { useMemo, useState } from 'react';
import { Helmet } from "react-helmet-async";
import {
    ConfigProvider,
    theme,
    Table,
    Input,
    Avatar,
    Tag,
    Button,
    Tooltip,
    Modal
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    Minus, Plus, CircleChevronDown, Funnel,
    Phone, Clock, MessageSquareDashed, Paperclip, Smile, Send,
    CircleArrowUp, Undo2, RefreshCw, UserRoundPlus,
    CirclePercent, Bot, Check, CircleHelp, Waypoints, PenTool, Mail,
    CircleX, Play, HelpCircle,
    X
} from 'lucide-react';

import Layout from '../../Layout'; // Giữ nguyên import layout của bạn
import { Link } from 'react-router-dom';
import { LeftOutlined } from "@ant-design/icons";
import { mixformatCurrency } from 'services/mixin';
import { ModalApprove } from './components/ModalApprove';
import { ModalDigitalSignature } from './components/ModalDigitalSignature';

const { TextArea } = Input;

interface CommentItem {
    id: number,
    author: string,
    text: any,
    time: string,
}

interface HistoryItem {
    status: number;
}

interface ExpenseDetail {
    status: number,
    step: number,
    history: HistoryItem[],
}

// 1. Định nghĩa map trạng thái
const STATUS_MAP: Record<number, { color: string; label: string }> = {
    1: { color: 'warning', label: 'Chờ duyệt' },
    2: { color: 'success', label: 'Đã duyệt' },
    3: { color: 'error', label: 'Không duyệt' },
};


// 1. Định nghĩa MAP chứa sẵn Icon Component
const STEP_MAP: Record<number, { color: string; label: string; icon: React.ReactNode }> = {
    1: { color: 'warning', label: 'Bước duyệt', icon: <Play size={10} className="inline-block mr-0.5" /> },
    2: { color: 'success', label: 'Bước duyệt', icon: <Check size={10} className="inline-block mr-0.5" /> },
    3: { color: 'error', label: 'Bước duyệt', icon: <CircleX size={10} className="inline-block mr-0.5" /> },
};

const tabs = [
    { id: 1, label: 'Thảo luận' },
    { id: 2, label: 'Đính kèm' },

]


export default function ExpenseDetail() {

    const [isDarkMode, setIsDarkMode] = useState(false);
    const [comments, setComments] = useState<CommentItem[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [tab, setTab] = useState(1)
    const [showModalApprove, setShowModalApprove] = useState(false)
    const [showModalDigitalSign, setShowModalDigitalSign] = useState(false)
    const [showModalUndo, setShowModalUndo] = useState(false)

    // ================= DỮ LIỆU MOCK =================
    const [detail, setDetail] = useState<ExpenseDetail>({
        status: 1,
        step: 1,
        history: [
            { status: 1 }
        ],
    });
    const stepInfo = STEP_MAP[detail?.step];



    const handleSendComment = () => {
        if (!inputValue.trim()) return;
        const newComment = { id: Date.now(), author: 'Mr. Huấn', text: inputValue, time: 'Vừa xong' };
        setComments([...comments, newComment]);
        setInputValue('');
    };


    // ====Collapse States====
    const [expandedBlocks, setExpandedBlocks] = useState({
        general: true,
        settings: true,
        detail: true,
        relate: true,
        history: true,
        comment: true,
    });

    const toggleBlock = (key: keyof typeof expandedBlocks) => {
        setExpandedBlocks(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // ====Định nghĩa cột====

    const columns2 = [
        {
            title: "Diễn giải",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Số lượng",
            dataIndex: "count",
            key: "count",
            align: 'right' as const
        },
        {
            title: "Số tiền",
            dataIndex: "ammount",
            key: "ammount",
            align: 'right' as const,
            render: (text: number) => (
                <span>{mixformatCurrency(text, true)}</span>
            ),
        },
        {
            title: "Thuế (%)",
            dataIndex: "percent",
            key: "percent",
            align: 'right' as const
        },
        {
            title: "Tiền thuế",
            dataIndex: "taxMoney",
            key: "taxMoney",
            align: 'right' as const,
            render: (text: number) => (
                <span>{mixformatCurrency(text, true)}</span>
            ),
        },
        {
            title: "Tổng tiền",
            dataIndex: "total",
            key: "total",
            align: 'right' as const,
            render: (text: number) => (
                <span>{mixformatCurrency(text, true)}</span>
            ),
        },
    ];

    // ================= MOCK DATA BẢNG =================

    const rawData1 = [
        {
            id: 1,
            name: 'Thanh toán đợt 1',
            count: 1,
            ammount: 15000000,
            percent: 8,
            taxMoney: 1200000,
            total: 16200000,
        },
        {
            id: 2,
            name: 'Thanh toán đợt 2',
            count: 1,
            ammount: 15000000,
            percent: 8,
            taxMoney: 1200000,
            total: 16200000,
        },
        {
            id: 3,
            name: 'Thanh toán đợt 3',
            count: 1,
            ammount: 15000000,
            percent: 8,
            taxMoney: 1200000,
            total: 16200000,
        },
    ];

    // ================= THEME & UI COMPONENTS =================
    const antdThemeConfig = {
        token: {
            colorPrimary: '#3e91ab',
            colorBgContainer: isDarkMode ? '#1f2937' : '#ffffff',
            colorText: isDarkMode ? '#f3f4f6' : '#374151',
            colorBorder: isDarkMode ? '#4b5563' : '#d1d5db',
            borderRadius: 6,
        },
        components: { Table: { headerBg: isDarkMode ? '#374151' : '#f9fafb' } },
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    };

    const CardHeader = ({ title, blockKey, extra }: { title: any, blockKey: keyof typeof expandedBlocks, extra?: any }) => (
        <div className="flex justify-between items-center mb-4 pb-2 border-b dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 px-4 py-2 -mx-4 -mt-4 rounded-t-lg">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100">{title}</h3>
            <div className="flex items-center gap-3">
                {extra}
                <div onClick={() => toggleBlock(blockKey)} className="cursor-pointer text-gray-400 hover:text-[#3e91ab] p-1">
                    {expandedBlocks[blockKey] ? <Minus size={16} /> : <Plus size={16} />}
                </div>
            </div>
        </div>
    );

    return (
        <Layout>
            <Helmet>
                <title> Chi tiết phiếu chi | Khothi</title>
            </Helmet>
            <ConfigProvider theme={antdThemeConfig}>
                <div className={`min-h-screen pb-10 transition-colors duration-300`}>

                    {/* --- HEADER ACTIONS --- */}
                    <div className="border-b dark:border-gray-700 px-4 md:px-6 py-3 flex flex-wrap justify-between items-center gap-4 sticky top-[64px] bg-white dark:bg-[#1f1f1f] z-10 shadow-sm">
                        <div className="flex sm:items-center gap-4 text-sm sm:flex-row flex-col items-start">
                            <div className="flex sm:items-center gap-4 text-sm items-start">
                                <Link to="/expense" className="flex items-center gap-2">
                                    <LeftOutlined className="w-6 h-6 flex items-center justify-center rounded-full bg-[#3e91ab] hover:bg-[#357f96] text-white" />
                                </Link>
                                <span className="font-medium text-[#3e91ab] border-b-2 border-[#3e91ab] pb-1 cursor-pointer">Thông tin chung</span>
                                <span className="text-gray-500 dark:text-gray-400 pb-1 font-medium">Quy trình duyệt</span>
                            </div>
                        </div>

                        <div className='overflow-x-auto no-scrollbar'>
                            <div className="flex items-center gap-4 xl:gap-5 w-full xl:w-auto ml-auto min-w-max text-000000e0">
                                <Button type="primary" icon={<Bot size={18} />}>
                                    1AI Monitor
                                </Button>
                                {detail.status === 2 ? (
                                    <button onClick={() => setShowModalUndo(true)} className="text-sm flex items-center gap-1.5 hover:text-[#3e91ab]"><Undo2 size={14} /> Hoàn duyệt</button>
                                ) : (
                                    <button onClick={() => setShowModalApprove(true)} className="text-sm flex items-center gap-1.5 hover:text-[#3e91ab]"><Check size={14} /> Duyệt</button>
                                )

                                }
                                <button className="text-sm flex items-center gap-1.5 hover:text-[#3e91ab]"><Waypoints size={14} /> Liên kết</button>
                                <button onClick={() => setShowModalDigitalSign(true)} className="flex items-center gap-1.5 hover:text-[#3e91ab] text-sm"><PenTool size={14} /> Ký số</button>
                                <button className="flex items-center gap-1.5 hover:text-[#3e91ab] text-sm"><Mail size={14} /> Email</button>
                                <button className="flex items-center gap-1.5 hover:text-[#3e91ab] text-sm"><CircleChevronDown size={20} /></button>
                            </div>
                        </div>
                    </div>

                    {/* --- MAIN LAYOUT --- */}
                    <div className="mx-auto mt-4 grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">

                        {/* CỘT TRÁI CÓ TABS CHÍNH */}
                        <div className="min-w-0 overflow-hidden">
                            <div className="flex flex-col gap-6 pt-0">

                                {/* 1 */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Thông tin chung" blockKey="general" />
                                    {expandedBlocks.general && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8 text-[14px] mt-4">

                                            {/* Cột 1 */}
                                            <div className="flex flex-col gap-5">
                                                <div><p className="text-gray-400 mb-1">Mã phiếu chi</p><p className="font-medium text-gray-800 dark:text-gray-200">PC_2025_604</p></div>
                                                <div><p className="text-gray-400 mb-1">Ngày chi</p><p className="text-gray-800 dark:text-gray-200">03/06/2025</p></div>

                                                <div>
                                                    <p className="text-gray-400 mb-1">Người tạo</p>
                                                    <div style={{ paddingLeft: '4px' }} className="inline-flex items-center  gap-1.5 px-2 py-0.5 rounded-full bg-[#f4f4f4] dark:bg-[#111827]">
                                                        <Avatar size={20} className="bg-gray-500 text-[10px]" src='https://trial.1office.vn/f/p/TDI0OHljd045SHpkUkF0d2REV0NRQ1pCZlJnU1dQUGZoTm5rT0dRMWVpYmFiREZsL2lDd1dNTFJwbEtUbnJjdVZVWkZ6T2FWU2ZSNkszZVpyeWxxWENWU29kQnBnNEgzWGM5NjVEL0xGNHVUSUNOWDRsaz0/1535570912638031ae4eb2b025045322.25.11.2022.small.png' />
                                                        <span className="text-000000e0">Lê Việt Thắng</span>
                                                    </div>
                                                </div>
                                                <div><p className="text-gray-400 mb-1">Tài khoản</p><p className="text-gray-800 dark:text-gray-200">Ngân hàng MB Bank (KA)</p></div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Hạch toán nội bộ</p>
                                                    <p className="text-gray-800 dark:text-gray-200">Hạch toán cho công việc</p>
                                                    <p className="text-gray-400 mb-1">
                                                        <span className='block text-xs text-[#3e91ab] dark:text-[#8adbf4]'>Họp phần mềm Office</span>
                                                    </p>
                                                </div>
                                                <div><p className="text-gray-400 mb-1">Hóa đơn</p><p className="text-gray-800 dark:text-gray-200">Có</p></div>
                                                <div><p className="text-gray-400 mb-1">Hóa đơn</p><p className="text-gray-800 dark:text-gray-200">2323 - 01/09/2026</p></div>


                                                <div>
                                                    <p className="text-gray-400 mb-1">Người duyệt</p>
                                                    <div className='flex gap-[5px]' style={{ flexWrap: 'wrap' }}>
                                                        <div style={{ paddingLeft: '4px' }} className="inline-flex items-center mb-2 gap-1.5 px-2 py-0.5 rounded-full bg-[#f4f4f4] dark:bg-[#111827]">
                                                            <Avatar size={20} className="bg-gray-500 text-[10px]" src='https://trial.1office.vn/f/p/TDI0OHljd045SHpkUkF0d2REV0NRQ1pCZlJnU1dQUGZoTm5rT0dRMWVpYmFiREZsL2lDd1dNTFJwbEtUbnJjdVZVWkZ6T2FWU2ZSNkszZVpyeWxxWENWU29kQnBnNEgzWGM5NjVEL0xGNHVUSUNOWDRsaz0/1535570912638031ae4eb2b025045322.25.11.2022.small.png' />
                                                            <span className="text-000000e0">Nguyễn Quang Huy RECO</span>
                                                        </div>
                                                        {/* <div style={{ paddingLeft: '4px' }} className="inline-flex items-center mb-2 gap-1.5 px-2 py-0.5 rounded-full bg-[#f4f4f4] dark:bg-[#111827]">
                                                            <Avatar size={20} className="bg-gray-500 text-[10px]" src='https://trial.1office.vn/f/p/TDI0OHljd045SHpkUkF0d2REV0NRQ1pCZlJnU1dQUGZoTm5rT0dRMWVpYmFiREZsL2lDd1dNTFJwbEtUbnJjdVZVWkZ6T2FWU2ZSNkszZVpyeWxxWENWU29kQnBnNEgzWGM5NjVEL0xGNHVUSUNOWDRsaz0/1535570912638031ae4eb2b025045322.25.11.2022.small.png' />
                                                            <span className="text-000000e0">Trần Nhật Minh RECO</span>
                                                        </div>
                                                        <div>
                                                            <Avatar size={24} className="border border-[#3e91ab] bg-transparent text-[#3e91ab] text-md   font-medium">+35</Avatar>
                                                        </div> */}
                                                    </div>
                                                </div>
                                                <div><p className="text-gray-400 mb-1">Ghi chú</p><p className="text-gray-800 dark:text-gray-200">--</p></div>

                                            </div>

                                            {/* Cột 2 */}
                                            <div className="flex flex-col gap-5">
                                                <div><p className="text-gray-400 mb-1">Nội dung chi</p><p className="text-gray-800 dark:text-gray-200">“Chi gói tuyển dụng TOPCV” (KA)</p></div>
                                                <div><p className="text-gray-400 mb-1">Ngày hạch toán</p><p className="text-gray-800 dark:text-gray-200">03/06/2025</p></div>
                                                <div><p className="text-gray-400 mb-1">Hình thức thanh toán</p><p className="text-gray-800 dark:text-gray-200">Ngân hàng</p></div>

                                                <div><p className="text-gray-400 mb-1">Kế hoạch chi</p><p className="text-[#3e91ab] dark:text-[#8adbf4]">“Chi gói tuyển dụng TOPCV” (KA)</p></div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Đối tượng chi</p>
                                                    <p className="text-gray-800 dark:text-gray-200">Chỉ trên một khách hàng</p>
                                                    <p className="text-gray-400 mb-1">
                                                        <span className='block text-xs text-[#3e91ab] dark:text-[#8adbf4]'>01129 - Trung Phong</span>
                                                    </p>
                                                </div>
                                                <div><p className="text-gray-400 mb-1">Lý do chi</p><p className="text-gray-800 dark:text-gray-200">Chi mua dịch vụ tuyển dụng</p></div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Người nhận tiền</p>
                                                    <p className="text-gray-800 dark:text-gray-200">Công ty Cổ phần TopCV</p>
                                                    <p className="text-gray-400 mb-1">
                                                        <span className='block text-xs'>Kỹ Thương Việt Nam</span>
                                                        <span className='block text-xs'>12345678</span>
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Trạng thái</p>
                                                    <Tag color={STATUS_MAP[detail?.status]?.color || 'default'}>
                                                        {STATUS_MAP[detail?.status]?.label || 'Không xác định'}
                                                    </Tag>
                                                </div>
                                                <div>
                                                    <p className="text-gray-400 mb-1">Bước duyệt</p>
                                                    <Tag color={STEP_MAP[detail?.step]?.color || 'default'}>
                                                        {STEP_MAP[detail?.step]?.label || 'Không xác định'}
                                                        {stepInfo?.icon || <HelpCircle size={12} className="inline-block mr-0.5" />}
                                                    </Tag>
                                                </div>

                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* COT 2 */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Trường tùy chỉnh" blockKey="settings" />
                                    {expandedBlocks.settings && (
                                        <span className="text-gray-500 dark:text-gray-400 text-sm">Không tìm thấy kết quả nào</span>
                                    )}
                                </div>

                                {/*  COT 3*/}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader title="Chi tiết" blockKey="detail" />
                                    {expandedBlocks.detail && (
                                        <div className="animate-in fade-in duration-300">
                                            {/* Ant Design Table */}
                                            <ConfigProvider
                                                theme={{
                                                    components: {
                                                        Table: {
                                                            headerBg: "#f8f9fa",
                                                            headerColor: "#6b7280",
                                                            cellPaddingBlock: 12,
                                                            rowHoverBg: "#eff6ff",
                                                        },
                                                    },
                                                }}
                                            >
                                                <Table
                                                    rowKey='id'
                                                    columns={columns2}
                                                    dataSource={rawData1}
                                                    pagination={false}
                                                    scroll={{ x: "max-content" }}
                                                    expandable={{
                                                        defaultExpandAllRows: true,
                                                        showExpandColumn: false, // Ẩn nút expand để hiển thị phẳng giống như UI ảnh gốc
                                                    }}
                                                    summary={() => (
                                                        <Table.Summary fixed>
                                                            <Table.Summary.Row className="font-bold bg-white border-t">
                                                                <Table.Summary.Cell index={0}></Table.Summary.Cell>
                                                                <Table.Summary.Cell index={1}></Table.Summary.Cell>
                                                                <Table.Summary.Cell index={2}></Table.Summary.Cell>
                                                                <Table.Summary.Cell index={3}></Table.Summary.Cell>
                                                                <Table.Summary.Cell index={4} align="right">Tổng</Table.Summary.Cell>
                                                                <Table.Summary.Cell index={5} align="right">{mixformatCurrency(59400000, true)}</Table.Summary.Cell>
                                                            </Table.Summary.Row>
                                                        </Table.Summary>
                                                    )}
                                                />
                                            </ConfigProvider>
                                        </div>
                                    )}
                                </div>

                                {/*  COT 4*/}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                    <CardHeader
                                        title="Liên quan"
                                        blockKey="relate"
                                        extra={<Waypoints size={16} className="cursor-pointer text-gray-400 hover:text-[#3e91ab]" />}
                                    />
                                    {expandedBlocks.relate && (
                                        <div className="animate-in fade-in duration-300 gap-3" style={{ display: 'flex', flexDirection: 'column' }}>
                                            <div className="shadow-[0_1px_10px_0_rgba(0,0,0,0.16)] hover:shadow-none dark:shadow-none border border-transparent hover:border-slate-200 dark:border-slate-700/60 rounded-sm p-4 bg-white dark:bg-slate-800/40 transition-all duration-200">
                                                {/* Header: Avatar + Title + Subtitle */}
                                                <div className="flex items-start gap-3 mb-4">
                                                    <div className="w-8 h-8 rounded-full bg-[#0087BF] flex-shrink-0 flex items-center justify-center text-white font-medium text-sm">
                                                        R
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <h4 className="text-[14px] font-medium text-slate-800 dark:text-slate-100 leading-snug truncate">
                                                            ĐT.062025.147 - CÔNG TY TNHH VINCOM RETAIL MIỀN BẮC
                                                        </h4>
                                                        <div className="text-[11px] text-slate-400 dark:text-slate-400 flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                            <span>Nguyễn Quang Huy RECO</span>
                                                            <Clock size={11} className="inline-block" />
                                                            <span>15:18 ng 18/8/2026</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Body Details: Responsive Grid */}
                                                <div className="grid grid-cols-[110px_1fr] sm:grid-cols-[130px_1fr] gap-y-2.5 text-[13px] pl-11">
                                                    {/* Row 1 */}
                                                    <div className="text-slate-500 dark:text-slate-400">Đối tượng</div>
                                                    <div className="text-slate-800 dark:text-slate-200 font-normal">Đối tác</div>

                                                    {/* Row 2 */}
                                                    <div className="text-slate-500 dark:text-slate-400">Điện thoại</div>
                                                    <div className="text-slate-400 dark:text-slate-500">--</div>

                                                    {/* Row 3 */}
                                                    <div className="text-slate-500 dark:text-slate-400 flex items-center">Người phụ trách đối tác</div>
                                                    <div>
                                                        <div className="w-6 h-6 rounded-full bg-[#0087BF] flex-shrink-0 flex items-center justify-center text-white font-medium text-sm">
                                                            C
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="shadow-[0_1px_10px_0_rgba(0,0,0,0.16)] hover:shadow-none dark:shadow-none border border-transparent hover:border-slate-200 dark:border-slate-700/60 rounded-sm p-4 bg-white dark:bg-slate-800/40 transition-all duration-200">
                                                {/* Header: Avatar + Title + Subtitle */}
                                                <div className="flex items-start gap-3 mb-4">
                                                    <div className="w-8 h-8 rounded-full bg-[#0087BF] flex-shrink-0 flex items-center justify-center text-white font-medium text-sm">
                                                        R
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <h4 className="text-[14px] font-medium text-slate-800 dark:text-slate-100 leading-snug truncate">
                                                            Trung Phong
                                                        </h4>
                                                        <div className="text-[11px] text-slate-400 dark:text-slate-400 flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                            <span>Admin Hệ Thống</span>
                                                            <Clock size={11} className="inline-block" />
                                                            <span>15:18 ng 18/8/2026</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Body Details: Responsive Grid */}
                                                <div className="grid grid-cols-[110px_1fr] sm:grid-cols-[130px_1fr] gap-y-2.5 text-[13px] pl-11">
                                                    {/* Row 1 */}
                                                    <div className="text-slate-500 dark:text-slate-400">Đối tượng</div>
                                                    <div className="text-slate-800 dark:text-slate-200 font-normal">Khách hàng</div>

                                                    {/* Row 2 */}
                                                    <div className="text-slate-500 dark:text-slate-400">Điện thoại</div>
                                                    <div>
                                                        <div style={{ paddingLeft: '4px' }} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/30 text-[11px] font-medium">
                                                            <Avatar size={20} className="bg-[#f96800] text-[10px]"><Phone size={15}></Phone></Avatar>
                                                            <span className="text-orange-600 dark:text-orange-400">0906336162</span>
                                                        </div>
                                                    </div>

                                                    <div className="text-slate-500 dark:text-slate-400">Nguồn khách hàng</div>
                                                    <div className="text-slate-800 dark:text-slate-200 font-normal">Facebook</div>

                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                            </div>
                        </div>

                        {/* CỘT PHẢI (THẢO LUẬN / LỊCH SỬ) - Giữ nguyên logic cũ */}
                        <div className="min-w-0 flex flex-col gap-4 h-full">

                            <div className="min-w-0 bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                <CardHeader
                                    title="Lịch sử hoạt động"
                                    blockKey="history"
                                    extra={<Funnel size={16} className="cursor-pointer text-gray-400 hover:text-[#3e91ab]" />}
                                />
                                {expandedBlocks.history && (
                                    <div className="pb-4 mt-4">
                                        {/* Khung chứa Timeline */}
                                        <div className="relative pl-6 space-y-4 max-h-[450px] overflow-y-auto pr-1">

                                            {/* Trục dọc (Đường line màu xám chạy dọc timeline) */}
                                            <div className="absolute left-[11px] top-3 bottom-3 w-0.5 bg-gray-200 dark:bg-gray-700"></div>

                                            {/* --- ITEM TIMELINE 1 --- */}
                                            {detail.history.map(item => (
                                                <div className="relative flex items-start group">
                                                    {item.status === 1 &&
                                                        <div className="absolute -left-6 top-2 w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center shadow-sm z-9">
                                                            <Plus size={12} />
                                                        </div>}
                                                    {item.status === 2 &&
                                                        <div className="absolute -left-6 top-2 w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center shadow-sm z-9">
                                                            <Check size={12} />
                                                        </div>}
                                                    {item.status === 3 &&
                                                        <div className="absolute -left-6 top-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow-sm z-9">
                                                            <X size={12} />
                                                        </div>}
                                                    {item.status === 4 &&
                                                        <div className="absolute -left-6 top-2 w-6 h-6 rounded-full bg-pink-500 text-white flex items-center justify-center shadow-sm z-9">
                                                            <Undo2 size={12} />
                                                        </div>}

                                                    {/* Card nội dung */}
                                                    <div className="flex-1 bg-[#f8f9fa] dark:bg-gray-800 p-4 rounded-md border border-gray-100 dark:border-gray-700 relative ml-2">
                                                        <div className="flex justify-between items-start">
                                                            <div>
                                                                <p className="text-[13px] text-gray-800 dark:text-gray-200">
                                                                    <span className="font-bold">Mr.Huân</span> <span className="text-gray-500"> 
                                                                        {item.status === 1 && 'đã tạo mới'}
                                                                        {item.status === 2 && 'đã duyệt'}
                                                                        {item.status === 3 && 'đã từ chối'}
                                                                        {item.status === 4 && 'đã hoàn lại'}
                                                                    </span>
                                                                </p>
                                                                <p className="text-[11px] text-gray-400 mt-0.5 flex items-center">
                                                                    <span className="mr-1"></span> 17:26 ng 25/06/2026
                                                                </p>
                                                            </div>
                                                            {/* Avatar chữ B bên phải */}
                                                            <div className="w-8 h-8 rounded-full bg-gray-400 text-white flex items-center justify-center text-xs font-bold shrink-0 ml-3">
                                                                H
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}

                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 md:p-6 border dark:border-gray-700">
                                <div className="flex justify-between items-center mb-4 pb-2 border-b dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 px-4 py-2 -mx-4 -mt-4 rounded-t-lg">
                                    <div className="flex sm:items-center gap-4 text-sm sm:flex-row flex-col items-start">
                                        {tabs.map((item) => (
                                            <button
                                                type="button"
                                                onClick={() => setTab(item.id)}
                                                className={`font-medium pb-1 border-b-2 transition-colors ${item.id === tab
                                                    ? "text-[#3e91ab] border-[#3e91ab]"
                                                    : "text-gray-500 dark:text-gray-400 border-transparent"
                                                    }`}
                                            >
                                                {item.label}
                                            </button>
                                        ))}
                                    </div>
                                    <div onClick={() => toggleBlock('comment')} className="cursor-pointer text-gray-400 hover:text-[#3e91ab] p-1">
                                        {expandedBlocks['comment'] ? <Minus size={16} /> : <Plus size={16} />}
                                    </div>
                                </div>
                                {expandedBlocks.comment && (
                                    <div className="flex flex-col mt-2 relative">

                                        {/* Khu vực hiển thị bình luận */}
                                        <div className="flex-1 overflow-y-auto mb-4 pr-2">
                                            {comments.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center text-gray-400 opacity-60">
                                                    <MessageSquareDashed size={48} className="mb-3" strokeWidth={1} />
                                                    <p className="text-sm">Không có thảo luận nào</p>
                                                </div>
                                                // EMPTY STATE NẾU KHÔNG CÓ BÌNH LUẬN
                                            ) : (
                                                // NẾU CÓ BÌNH LUẬN
                                                comments.map(comment => (
                                                    <div key={comment.id} className="flex gap-3 mb-6">
                                                        <Avatar style={{ backgroundColor: '#84cc16' }}>{comment.author.charAt(0)}</Avatar>
                                                        <div className="flex-1">
                                                            <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm">{comment.author}</span>
                                                            <div className="bg-gray-100 dark:bg-gray-700/50 p-3 rounded-lg rounded-tl-none mt-1 text-sm text-gray-800 w-fit">
                                                                {comment.text}
                                                            </div>
                                                            <div className="flex items-center gap-4 mt-2 text-[11px] text-gray-500">
                                                                <span>{comment.time}</span>
                                                                <span className="cursor-pointer hover:text-[#3e91ab]">Thích</span>
                                                                <span className="cursor-pointer hover:text-[#3e91ab]">Trả lời</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>

                                        {/* Ô Input nhập liệu */}
                                        <div className="flex justify-between border border-gray-200 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-700/30 shrink-0 mt-auto">
                                            <div className="flex items-center gap-2 mb-2">
                                                <CircleArrowUp className="cursor-pointer text-gray-400 hover:text-[#3e91ab]" size={16} />
                                                <Input
                                                    bordered={false}
                                                    placeholder="Viết thảo luận..."
                                                    className="flex-1 text-sm"
                                                    value={inputValue}
                                                    onChange={(e) => setInputValue(e.target.value)}
                                                    onPressEnter={handleSendComment}
                                                />
                                            </div>
                                            <div className="flex gap-3 items-center text-gray-400 pl-8 pr-2">
                                                <Paperclip size={16} className="cursor-pointer hover:text-[#3e91ab]" />
                                                <Smile size={16} className="cursor-pointer hover:text-[#3e91ab]" />
                                                <Send size={16} className="cursor-pointer text-[#3e91ab]" />
                                            </div>
                                        </div>

                                    </div>
                                )}

                            </div>
                        </div>
                    </div>

                    {/* APPROVE  MODAL */}
                    {showModalApprove && (
                        <ModalApprove
                            open={showModalApprove}
                            detail={detail}
                            onApprove={() => {
                                setDetail((prev) => ({
                                    ...prev,
                                    status: 2,
                                    step: 2,
                                    history: [...prev.history, { status: 2 }],
                                }));

                                setShowModalApprove(false);
                            }}
                            onReject={() => {
                                setDetail((prev) => ({
                                    ...prev,
                                    status: 3,
                                    step: 3,
                                    history: [...prev.history, { status: 3 }],
                                }));

                                setShowModalApprove(false);
                            }}
                            onClose={() => setShowModalApprove(false)}
                        />
                    )}

                    {/* Digital Signature Modal */}
                    {showModalDigitalSign && (
                        <ModalDigitalSignature open={showModalDigitalSign} onClose={()=>setShowModalDigitalSign(false)}/>
                    )}

                    {/* REJECT  MODAL */}
                    {showModalUndo && (
                        <Modal
                            open={showModalUndo}
                            onCancel={() => setShowModalUndo(false)}
                            title='XÁC NHẬN'
                            footer={[
                                <div className="flex gap-5 justify-end" style={{ flexWrap: 'wrap' }}>
                                    <Button
                                        onClick={() => {
                                            setDetail((prev) => ({
                                                ...prev,
                                                status: 1,
                                                step: 1,
                                                history: [...prev.history, { status: 4 }],
                                            }));

                                            setShowModalUndo(false);
                                        }}
                                        type="primary"
                                        className="px-5 py-2.5 text-white font-medium rounded-md text-xs transition-colors"
                                    >
                                        ĐỒNG Ý
                                    </Button>
                                    <Button
                                        className="px-5 py-2.5 dark:text-white font-medium rounded-md text-xs transition-colors"
                                        style={{ background: 'transparent' }}
                                        onClick={() => setShowModalUndo(false)}
                                    >
                                        HỦY BỎ
                                    </Button>

                                </div>
                            ]}
                        >
                            <p className="text-gray-800 dark:text-gray-200">Bạn có chắc muốn hoàn duyệt không?</p>
                        </Modal>
                    )}

                </div>
            </ConfigProvider>
        </Layout>
    );
}